import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Database, Power, GraduationCap, Users, SmartphoneNfc, ArrowLeftRight,
  CreditCard, Sparkles, ShieldCheck, AppWindow, Wallet, Wifi, Images,
  AtSign, Mail, RefreshCw, Stethoscope, ArrowRight, Phone, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappLinks } from "@/lib/whatsapp";

const SERVICES = [
  { icon: Database,        title: "Datenübertragung",                 priceFrom: false, price: 54, hint: "Komplette Backup-Übertragung von Gerät zu Gerät." },
  { icon: Power,           title: "Handy startklar machen",           priceFrom: false, price: 5,  hint: "Einrichtung beim ersten Start — direkt im Geschäft." },
  { icon: GraduationCap,   title: "Handy-Grundschulung",              priceFrom: false, price: 10, hint: "Persönliche Einweisung in 30 Min." },
  { icon: Users,           title: "Kontakte kopieren",                priceFrom: false, price: 10, hint: "Adressbuch sicher auf neues Gerät übertragen." },
  { icon: SmartphoneNfc,   title: "SIM-Kartentausch",                 priceFrom: true,  price: 10, hint: "eSIM oder Nano-/Micro-SIM, je nach Gerät." },
  { icon: ArrowLeftRight,  title: "Tarifwechsel Prepaid",             priceFrom: false, price: 5,  hint: "Wir wechseln den Tarif gemeinsam mit Ihnen." },
  { icon: CreditCard,      title: "Wertkartenregistrierung",          priceFrom: false, price: 5,  hint: "Inkl. Identitätsprüfung." },
  { icon: Sparkles,        title: "Handyreinigung",                   priceFrom: false, price: 20, hint: "Profi-Reinigung von Display, Ports & Lautsprecher." },
  { icon: ShieldCheck,     title: "Displayschutzfolie anbringen",     priceFrom: false, price: 5,  hint: "Blasen­frei, perfekt zugeschnitten." },
  { icon: AppWindow,       title: "App installieren",                 priceFrom: false, price: 4,  hint: "Inkl. Konto-Anmeldung & Erst-Konfiguration." },
  { icon: Wallet,          title: "Guthaben aufladen",                priceFrom: false, price: 2,  hint: "A1 · MTel · alle gängigen Anbieter." },
  { icon: Wifi,            title: "Interneteinstellungen einrichten", priceFrom: false, price: 10, hint: "APN, WLAN, Hotspot — alles fertig konfiguriert." },
  { icon: Images,          title: "Bilder übertragen",                priceFrom: false, price: 15, hint: "Alle Fotos & Videos sicher auf neues Gerät." },
  { icon: AtSign,          title: "Google-Konto erstellen",           priceFrom: false, price: 15, hint: "Inkl. Wiederherstellungs-Setup." },
  { icon: Mail,            title: "E-Mail-Konto erstellen",           priceFrom: false, price: 20, hint: "Einrichtung & Synchronisation Ihres Postfachs." },
  { icon: RefreshCw,       title: "Software-Updates installieren",    priceFrom: false, price: 5,  hint: "OS- und App-Updates — sicher & vollständig." },
  { icon: Stethoscope,     title: "Smartphone-Diagnose",              priceFrom: false, price: 20, hint: "Komplett-Check inkl. Akku, Display, Funkmodul." },
];

export default function ZusatzleistungenPage() {
  return (
    <div className="bg-[#0A0A0B] min-h-screen" data-testid="zusatzleistungen-page">

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#DC1928]/8 blur-[120px] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Zusatzleistungen</div>
              <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
                Mehr Service.<br/>
                <span className="text-zinc-500">Mehr Möglichkeiten.</span>
              </h1>
            </div>
            <div className="md:col-span-5 md:pb-4">
              <p className="text-lg text-zinc-400 leading-relaxed">
                Vom SIM-Tausch über Datenübertragung bis zur kompletten Diagnose —
                wir nehmen Ihnen jede Smartphone-Aufgabe ab. Vor Ort. Persönlich. Fair.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Grid */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 md:gap-4">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.title} service={s} index={i} />
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-10 rounded-2xl border border-white/8 bg-[#121215] p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-zinc-400">
            <TrustItem dot="bg-emerald-500" label="Sofort vor Ort durchführbar" />
            <TrustItem dot="bg-amber-400" label="Festpreise · keine versteckten Kosten" />
            <TrustItem dot="bg-[#FF2D40]" label="Termin oder Walk-in — beides möglich" />
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-[28px] bg-gradient-to-br from-[#180409] via-[#0F0F12] to-[#0A0A0B] border border-white/8 p-8 md:p-14 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#DC1928]/15 blur-3xl"></div>
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Brauchen Sie etwas Spezielles?</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-3 leading-tight">
                  Sagen Sie uns, was Sie brauchen — wir machen es möglich.
                </h2>
                <p className="text-zinc-400 mt-3 max-w-lg">
                  Ihre Aufgabe ist nicht in der Liste? Kein Problem. Fragen Sie kurz per WhatsApp oder Telefon an — Beratung ist kostenlos.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
                <a href="tel:+436764440304" data-testid="zusatzleistungen-call" className="w-full md:w-auto">
                  <Button className="w-full md:w-auto bg-white text-[#0A0A0B] hover:bg-zinc-100 rounded-full h-12 px-7 btn-lift gap-1.5">
                    <Phone className="w-4 h-4" /> +43 676 4440304
                  </Button>
                </a>
                <a {...whatsappLinks.generic()} target="_blank" rel="noreferrer" data-testid="zusatzleistungen-wa" className="w-full md:w-auto">
                  <Button variant="outline" className="w-full md:w-auto bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 hover:text-white rounded-full h-12 px-7 gap-1.5">
                    <MessageCircle className="w-4 h-4" /> WhatsApp schreiben
                  </Button>
                </a>
                <Link to="/kontakt" data-testid="zusatzleistungen-contact" className="w-full md:w-auto">
                  <Button variant="outline" className="w-full md:w-auto rounded-full h-12 px-7 bg-transparent border-white/15 text-white hover:bg-white/10 hover:text-white gap-1.5">
                    Vor-Ort-Termin <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ service, index }) {
  const { icon: Icon, title, price, priceFrom, hint } = service;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl bg-gradient-to-br from-[#141417] to-[#0F0F12] border border-white/8 p-5 md:p-6 hover:border-[#DC1928]/40 transition-colors duration-300 overflow-hidden"
      data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* Subtle red glow on hover */}
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#FF2D40]/0 group-hover:bg-[#FF2D40]/10 blur-2xl transition-colors duration-500 pointer-events-none"></div>

      <div className="relative">
        {/* Icon + Price row */}
        <div className="flex items-start justify-between gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#DC1928]/10 border border-[#DC1928]/20 flex items-center justify-center text-[#FF2D40] shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-bold">{priceFrom ? "ab" : "Festpreis"}</div>
            <div className="font-display font-bold text-white numeric mt-0.5 text-2xl md:text-3xl tracking-tight">
              {priceFrom && <span className="text-zinc-500 mr-0.5">ab</span>}
              {price}<span className="text-zinc-500 ml-0.5">€</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-display text-base md:text-lg font-semibold text-white mt-5 leading-tight">
          {title}
        </h3>

        {/* Hint */}
        <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
          {hint}
        </p>
      </div>
    </motion.div>
  );
}

function TrustItem({ dot, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
      <span>{label}</span>
    </div>
  );
}
